import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, MapPin, Phone, MessageCircle, Check, Navigation, X, Users, Route, Clock, Wallet, Calendar, Plane } from "lucide-react";

const STEPS = ["on_my_way", "arrived", "started", "completed"];

// Build a Waze deep link — prefers coords, falls back to address search
function wazeUrl({ lat, lng, address }) {
  if (typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng)) {
    return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  }
  const q = encodeURIComponent(address || "");
  return `https://waze.com/ul?q=${q}&navigate=yes`;
}

function formatDuration(min) {
  if (!min || isNaN(min)) return null;
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}min` : `${h}h`;
}

export default function TripDetail() {
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { t, lang } = useLang();
  const nav = useNavigate();

  const load = () => api.get(`/driver/bookings/${id}`).then((r) => setTrip(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!trip) return <div className="p-6 text-slate-500">Loading…</div>;

  const verify = async () => {
    try { await api.post(`/driver/bookings/${id}/verify`, { code }); setVerified(true); toast.success("✓"); }
    catch (e) { toast.error(formatApiError(e)); setVerified(false); }
  };
  const setStep = async (step) => {
    try {
      await api.post(`/driver/bookings/${id}/status`, { status: step });
      toast.success(t(step === "started" ? "trip_started" : step === "completed" ? "trip_completed" : step === "arrived" ? "arrived" : "on_my_way"));
      // Open Waze on transitions where the driver moves
      if (step === "on_my_way") {
        const u = wazeUrl({ lat: trip.pickup_lat, lng: trip.pickup_lng, address: trip.pickup_address });
        window.open(u, "_blank", "noopener");
      } else if (step === "started") {
        const u = wazeUrl({ lat: trip.destination_lat, lng: trip.destination_lng, address: trip.destination_address });
        window.open(u, "_blank", "noopener");
      }
      if (step === "completed") setTimeout(() => nav("/driver"), 800);
      else load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/bookings/${id}/accept`);
      toast.success("Viagem aceite ✓");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Indique um motivo");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/driver/bookings/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Viagem rejeitada");
      setTimeout(() => nav("/driver"), 600);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const currentStep = trip.trip_status ? STEPS.indexOf(trip.trip_status) : -1;
  const dt = new Date(trip.service_datetime);
  const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  // Needs a response only if trip is assigned but the driver has neither accepted nor rejected yet.
  const needsResponse = trip.status === "assigned" && !trip.driver_response;

  return (
    <div className="p-5">
      <Link to="/driver" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#f59e0b] mb-4" data-testid="trip-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> {t("today")}</Link>

      <div className="mb-4">
        <div className="inline-block text-[10px] uppercase tracking-wider border border-[#f59e0b] text-[#f59e0b] px-2 py-0.5 mb-2">{trip.type}</div>
        <div className="font-display text-3xl text-[#0a0f1e]">{dt.toLocaleTimeString(lang === "PT" ? "pt-PT" : "en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
        <div className="text-sm text-slate-500">{dt.toLocaleDateString(lang === "PT" ? "pt-PT" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
      </div>

      {/* Trip summary — payout, distance, duration, pax, date/time */}
      <div className="mb-4 border-2 border-[#0a0f1e] bg-[#0a0f1e] text-white p-4" data-testid="trip-summary">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f59e0b] mb-1">
              <Wallet className="w-3 h-3" /> Valor líquido
            </div>
            <div className="font-display text-2xl" data-testid="trip-payout">
              €{Number(trip.driver_payout_eur ?? trip.driver_earning ?? 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f59e0b] mb-1">
              <Users className="w-3 h-3" /> Passageiros
            </div>
            <div className="font-display text-2xl" data-testid="trip-pax">
              {trip.passengers ?? "—"}<span className="text-sm opacity-70"> pax</span>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f59e0b] mb-1">
              <Route className="w-3 h-3" /> Distância
            </div>
            <div className="font-display text-xl" data-testid="trip-distance">
              {trip.distance_km ? `${Number(trip.distance_km).toFixed(1)} km` : "—"}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f59e0b] mb-1">
              <Clock className="w-3 h-3" /> Duração
            </div>
            <div className="font-display text-xl" data-testid="trip-duration">
              {formatDuration(trip.duration_min) || "—"}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f59e0b] mb-1">
            <Calendar className="w-3 h-3" /> Dia e hora
          </div>
          <div className="font-display text-lg" data-testid="trip-datetime">
            {dt.toLocaleDateString(lang === "PT" ? "pt-PT" : "en-GB", { weekday: "short", day: "2-digit", month: "short" })}
            <span className="mx-2 opacity-40">·</span>
            {dt.toLocaleTimeString(lang === "PT" ? "pt-PT" : "en-GB", { hour: "2-digit", minute: "2-digit" })}
          </div>
          {trip.flight_number && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#f59e0b] font-mono" data-testid="trip-flight">
              <Plane className="w-3 h-3" /> Voo {trip.flight_number}
            </div>
          )}
        </div>
      </div>

      {/* ACCEPT / REJECT — shown only while driver has not responded */}
      {needsResponse && !showRejectPrompt && (
        <div className="mb-4 border-2 border-[#f59e0b] bg-[#fffaf0] p-4" data-testid="trip-response-panel">
          <div className="label-caps text-[#f59e0b] mb-2">Nova viagem — responda</div>
          <div className="text-sm text-slate-700 mb-4">Aceite para iniciar o serviço ou rejeite se estiver indisponível.</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={accept}
              disabled={busy}
              data-testid="trip-accept-btn"
              className="h-12 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4" /> Aceitar
            </button>
            <button
              onClick={() => setShowRejectPrompt(true)}
              disabled={busy}
              data-testid="trip-reject-btn"
              className="h-12 border border-slate-300 text-slate-700 font-semibold uppercase text-xs tracking-wide flex items-center justify-center gap-2 hover:border-red-500 hover:text-red-500 disabled:opacity-50"
            >
              <X className="w-4 h-4" /> Rejeitar
            </button>
          </div>
        </div>
      )}

      {/* Reject reason prompt */}
      {needsResponse && showRejectPrompt && (
        <div className="mb-4 border-2 border-red-400 bg-red-50 p-4" data-testid="trip-reject-panel">
          <div className="label-caps text-red-600 mb-2">Motivo da rejeição</div>
          <textarea
            className="w-full h-20 border border-slate-300 p-2 text-sm mb-3"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex: já tenho outra viagem no mesmo horário"
            data-testid="trip-reject-reason"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => { setShowRejectPrompt(false); setRejectReason(""); }}
              disabled={busy}
              className="h-11 border border-slate-300 text-slate-600 uppercase text-xs tracking-wide"
              data-testid="trip-reject-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={reject}
              disabled={busy || !rejectReason.trim()}
              className="h-11 bg-red-500 text-white font-semibold uppercase text-xs tracking-wide disabled:opacity-50"
              data-testid="trip-reject-confirm"
            >
              Confirmar rejeição
            </button>
          </div>
        </div>
      )}

      {/* Already-accepted badge */}
      {trip.driver_response === "accepted" && (
        <div className="mb-4 border border-emerald-400 bg-emerald-50 px-4 py-2 text-emerald-700 text-xs uppercase tracking-wide flex items-center gap-2" data-testid="trip-accepted-badge">
          <Check className="w-4 h-4" /> Viagem aceite
        </div>
      )}

      {/* Client card */}
      <div className="border border-slate-200 bg-white p-4 mb-4">
        <div className="text-xs label-caps mb-2">Client</div>
        <div className="font-display text-xl text-[#0a0f1e]">{trip.client_name}</div>
        <div className="text-xs text-slate-500 mt-1">{trip.passengers} pax · {trip.luggage} luggage</div>
        {trip.flight_number && <div className="text-xs text-[#f59e0b] mt-1 font-mono">✈ {trip.flight_number}</div>}
        <div className="mt-4 flex gap-2">
          <a href={`tel:${trip.client_phone}`} className="flex-1 border border-slate-200 py-2.5 text-xs uppercase text-slate-700 hover:border-[#f59e0b] hover:text-[#f59e0b] flex items-center justify-center gap-1"><Phone className="w-3 h-3" /> Call</a>
          <a href={`https://wa.me/${(trip.client_phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 border border-slate-200 py-2.5 text-xs uppercase text-slate-700 hover:border-[#f59e0b] hover:text-[#f59e0b] flex items-center justify-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp</a>
        </div>
      </div>

      {/* Locations */}
      <div className="space-y-3 mb-4">
        <LocCard label={t("pickup")} address={trip.pickup_address} mapsUrl={mapsUrl(trip.pickup_address)} accent />
        <LocCard label={t("destination")} address={trip.destination_address} mapsUrl={mapsUrl(trip.destination_address)} />
      </div>

      {trip.special_requests && (
        <div className="border-l-2 border-[#f59e0b] bg-white p-3 mb-4">
          <div className="label-caps mb-1">Special request</div>
          <div className="text-sm text-slate-700">{trip.special_requests}</div>
        </div>
      )}

      {/* Security code — only after driver accepts */}
      {trip.driver_response === "accepted" && (
        <div className="border border-[#f59e0b]/40 bg-white p-4 mb-4">
          <div className="label-caps text-[#f59e0b] mb-3">{t("security_code")}</div>
          {verified ? (
            <div className="flex items-center gap-2 text-emerald-400">
              <Check className="w-5 h-5" /> <span className="font-display text-xl">Verified</span>
            </div>
          ) : (
            <>
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg border-slate-200 focus:border-[#f59e0b]" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              <button onClick={verify} disabled={code.length !== 6} data-testid="verify-btn"
                className="w-full mt-4 h-11 bg-[#f59e0b] text-[#0a0f1e] font-semibold uppercase text-xs tracking-wide disabled:opacity-40">
                {t("verify")}
              </button>
            </>
          )}
        </div>
      )}

      {/* Status flow — only after driver accepts */}
      {trip.driver_response === "accepted" && (
        <div className="space-y-2">
          {STEPS.map((s, i) => {
            const done = i <= currentStep;
            const active = i === currentStep + 1;
            // OTP verify is only required to start the trip (pickup verification).
            // "A caminho" and "Cheguei" must be tap-able without verify so Waze can open.
            const disabled = done || (s === "started" && !verified);
            return (
              <button key={s} onClick={() => setStep(s)} disabled={disabled}
                data-testid={`step-${s}`}
                className={`w-full h-14 flex items-center justify-between px-5 border transition-all ${
                  done ? "border-emerald-400/30 text-emerald-400 bg-emerald-400/5" :
                  active ? "border-[#f59e0b] bg-[#f59e0b] text-[#0a0f1e]" :
                  "border-slate-200 text-slate-500"
                }`}>
                <span className="uppercase text-xs tracking-wide font-semibold">{t(s === "started" ? "trip_started" : s === "completed" ? "trip_completed" : s === "arrived" ? "arrived" : "on_my_way")}</span>
                {done && <Check className="w-5 h-5" />}
                {active && <span className="text-[10px] font-bold">TAP</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LocCard({ label, address, mapsUrl, accent }) {
  return (
    <a href={mapsUrl} target="_blank" rel="noreferrer" className={`block border ${accent ? "border-[#f59e0b]" : "border-slate-200"} bg-white p-4 hover:bg-slate-50`}>
      <div className="flex items-start gap-3">
        <MapPin className={`w-5 h-5 ${accent ? "text-[#f59e0b]" : "text-slate-500"} mt-0.5`} />
        <div className="flex-1">
          <div className="label-caps mb-1">{label}</div>
          <div className="text-sm text-[#0a0f1e]">{address}</div>
        </div>
        <Navigation className="w-4 h-4 text-slate-500" />
      </div>
    </a>
  );
}
